// v14j — Begeleidende e-mail voor deze offerte.
// Laadt basis uit user.email_settings (default_subject/default_body/signature)
// met placeholder-vervanging {project_name}, {quote_number}, {client_name}.
// Emma kan een preview genereren die de gebruiker kan bewerken.
// Acties: kopiëren + openen in eigen mailprogramma (mailto:).
import React, { useEffect, useMemo, useState } from "react";
import { Mail, Sparkles, RefreshCw, Copy, ExternalLink, RotateCcw, ChevronDown, Send, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { useQuoteEmailSend, EmailSendPrompts, EmailDispatchHistory, SmtpStatusHints } from "./emailSend";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function _get(path) {
  const r = await fetch(`${API}${path}`, { credentials: "include" });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}
async function _post(path, body) {
  const r = await fetch(`${API}${path}`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}

/** Vervang {project_name} / {quote_number} / {client_name} in template-tekst. */
function _fillPlaceholders(text, vars) {
  return (text || "").replace(/\{(project_name|quote_number|client_name)\}/g,
    (m, k) => (vars[k] ?? "").toString());
}

/** v14j.1 — Voeg handtekening idempotent onderaan de body toe.
 * • Geen handtekening → body ongewijzigd.
 * • Lege body → alleen de handtekening.
 * • Handtekening reeds aanwezig (whitespace-normalised substring in laatste
 *   500 tekens) → NIET opnieuw toevoegen.
 * • Anders → body + "\n\n" + handtekening.
 */
function _appendSignatureOnce(body, signature, vars) {
  const sig = _fillPlaceholders(signature || "", vars).trim();
  if (!sig) return (body || "").replace(/\s+$/g, "");
  const bodyClean = (body || "").replace(/\s+$/g, "");
  if (!bodyClean) return sig;
  const norm = (s) => (s || "").replace(/\s+/g, " ").trim().toLowerCase();
  const tail = norm(bodyClean.slice(-800));
  if (tail.includes(norm(sig))) return bodyClean;
  return `${bodyClean}\n\n${sig}`;
}

function _buildDefaultBody(emailSettings, vars) {
  const body = _fillPlaceholders(emailSettings.default_body || "", vars);
  return _appendSignatureOnce(body, emailSettings.signature || "", vars);
}

function _buildDefaultSubject(emailSettings, vars) {
  return _fillPlaceholders(emailSettings.default_subject || "", vars);
}

/**
 * @param quoteId      — quote.id
 * @param quoteNumber  — quote_number (voor placeholders)
 * @param projectName  — project.name
 * @param clientName   — client.name
 * @param clientEmail  — client.email (voor mailto to-field)
 */
export default function CoverEmailPanel({
  quoteId, quoteNumber = "", projectName = "", clientName = "", clientEmail = "",
  clientId = "", onQuoteStatusChange,
}) {
  const [open, setOpen] = useState(false);
  const [emailSettings, setEmailSettings] = useState({});
  const [smtpConnected, setSmtpConnected] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [smtpCfg, setSmtpCfg] = useState({});
  const refreshSmtp = () => _get("/auth/me").then((me) => setSmtpCfg(me.smtp_config || {})).catch(() => {});
  const ctl = useQuoteEmailSend(quoteId, { onQuoteStatusChange, onFailed: refreshSmtp });
  const sending = ctl.sending;
  const [err, setErr] = useState("");
  const [aiGenerated, setAiGenerated] = useState(false);
  const hasClientEmail = !!(clientEmail || "").trim();

  const vars = useMemo(() => ({
    project_name: projectName, quote_number: quoteNumber, client_name: clientName,
  }), [projectName, quoteNumber, clientName]);

  const loadDefaults = (settings, replaceExisting = true) => {
    const s = settings || emailSettings;
    const nextSubject = _buildDefaultSubject(s, vars);
    const nextBody = _buildDefaultBody(s, vars);
    if (replaceExisting || !subject) setSubject(nextSubject);
    if (replaceExisting || !body) setBody(nextBody);
    setAiGenerated(false);
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await _get("/auth/me");
        const s = me.email_settings || {};
        setEmailSettings(s);
        setSmtpConnected(!!(me.smtp_config || {}).connected);
        setSmtpCfg(me.smtp_config || {});
        setSubject(_buildDefaultSubject(s, vars));
        setBody(_buildDefaultBody(s, vars));
      } catch (e) { /* silent */ }
      finally { setLoaded(true); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  const doAI = async () => {
    setBusy(true); setErr("");
    try {
      const r = await _post(`/quotes/${quoteId}/ai-generate-email`, { extra_context: "" });
      if (r.error) throw new Error(r.error);
      setSubject(r.subject || subject);
      // v14j.1 — Handtekening idempotent onderaan de AI-body zetten. Zo krijg
      // je nooit een dubbele afsluiting bij regenereren, zelfs als Emma zelf
      // al iets afsluit dat op de handtekening lijkt.
      const withSig = _appendSignatureOnce(r.body || body, emailSettings.signature || "", vars);
      setBody(withSig);
      setAiGenerated(true);
    } catch (e) {
      setErr(e.message || "Onbekende fout");
    } finally { setBusy(false); }
  };

  const doReset = () => {
    loadDefaults(emailSettings, true);
    toast.success("Standaardtekst hersteld");
  };

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${subject}\n\n${body}`);
      toast.success("E-mailtekst gekopieerd");
    } catch { toast.error("Kopiëren mislukt"); }
  };

  const doMailto = () => {
    const to = clientEmail || "";
    const href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
  };

  const doSend = async (confirmUnknown = false) => {
    if (!hasClientEmail) return;
    setErr("");
    const res = await ctl.send({ to: clientEmail, subject, body, confirmUnknown });
    if (res.cancelled) return;
    if (res.ok) toast.success(`Aanvaard door je mailserver (${res.attachment || "PDF-bijlage"}). Aflevering bij de klant is niet bevestigd.`);
    else if (res.message) { setErr(res.message); toast.error(res.message); }
  };

  if (!loaded) return null;

  return (
    <div className="border border-[#E5E2DC] rounded-2xl bg-white mb-4" data-testid="cover-email-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
        data-testid="cover-email-toggle"
      >
        <ChevronDown className={`w-4 h-4 text-[#B07A4F] transition-transform ${open ? "" : "-rotate-90"}`} />
        <Mail className="w-5 h-5 text-[#B07A4F]" />
        <span className="font-heading text-base font-semibold flex-1">
          Begeleidende e-mail voor deze offerte
        </span>
        {aiGenerated && (
          <span className="text-[10px] uppercase tracking-wider text-[#B07A4F] font-bold">Emma-versie</span>
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-[#F0EFEA]">
          <div className="flex flex-wrap gap-2 pt-3">
            <Button
              type="button"
              onClick={doAI}
              disabled={busy}
              className="bg-[#B07A4F] hover:bg-[#8f6440] text-white rounded-xl h-10"
              data-testid="cover-email-ai-btn"
            >
              {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {busy ? "Emma stelt op…" : (aiGenerated ? "Regenereer" : "Laat Emma e-mail opstellen")}
            </Button>
            <Button
              type="button"
              onClick={doReset}
              variant="outline"
              className="rounded-xl h-10 border-[#E5E2DC] text-[#5D7259]"
              data-testid="cover-email-reset-btn"
              title="Herstel naar standaardtekst uit Instellingen"
            >
              <RotateCcw className="w-4 h-4" /> Standaardtekst herstellen
            </Button>
          </div>


          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#4F4F4D]">
              Onderwerp <span className="text-[#A8A7A0] font-normal">(optioneel)</span>
            </label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Onderwerpregel"
              className="text-sm"
              data-testid="cover-email-subject"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#4F4F4D]">
              E-mailtekst <span className="text-[#A8A7A0] font-normal">(optioneel)</span>
            </label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              placeholder="Begeleidend bericht — je kan Emma laten opstellen of hier zelf typen."
              className="text-sm font-sans"
              data-testid="cover-email-body"
            />
          </div>

          <div className="text-[11px] text-[#5D7259] bg-[#FAF9F6] border border-[#F0EFEA] rounded-lg px-3 py-2">
            <strong>Voeg de PDF-offerte toe als bijlage in je mailprogramma.</strong> Je kan de PDF hierboven downloaden en handmatig aanhaken. Wanneer je hieronder rechtstreeks verstuurt via een gekoppelde mailbox, wordt de PDF automatisch als bijlage meegestuurd.
          </div>

          {/* v14k — Client-e-mail hint */}
          {!hasClientEmail && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2"
              data-testid="cover-email-no-recipient-hint">
              <AlertCircle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <span className="text-amber-800">Er is nog geen e-mailadres voor deze klant ingevuld.</span>
                {clientId && (
                  <Link to={`/klanten/${clientId}`}
                    className="ml-1 font-semibold text-amber-900 hover:underline"
                    data-testid="cover-email-add-recipient-link">
                    E-mailadres toevoegen →
                  </Link>
                )}
              </div>
            </div>
          )}

          <SmtpStatusHints cfg={smtpCfg} prefix="cover-email" />
          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="cover-email-error">
              Fout: {err}
            </div>
          )}
          <EmailSendPrompts ctl={ctl} onConfirmUnknown={() => doSend(true)} prefix="cover-email" />
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              type="button"
              onClick={doCopy}
              variant="outline"
              className="rounded-xl h-10 border-[#B07A4F] text-[#B07A4F] hover:bg-[#FFF3E5]"
              data-testid="cover-email-copy-btn"
            >
              <Copy className="w-4 h-4" /> Kopieer e-mailtekst
            </Button>
            <Button
              type="button"
              onClick={doMailto}
              className="bg-[#5D7259] hover:bg-[#4a5c47] text-white rounded-xl h-10"
              data-testid="cover-email-mailto-btn"
              title={clientEmail ? `Openen in mailprogramma (naar ${clientEmail})` : "Openen in eigen mailprogramma"}
            >
              <ExternalLink className="w-4 h-4" /> Open in mijn mailprogramma
            </Button>
            {smtpConnected && (
              <Button
                type="button"
                onClick={() => doSend(false)}
                disabled={!hasClientEmail || sending}
                className="bg-[#B07A4F] hover:bg-[#8f6440] text-white rounded-xl h-10 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="cover-email-send-btn"
                title={hasClientEmail ? `Rechtstreeks verzenden naar ${clientEmail} inclusief PDF` : "Klant-e-mailadres ontbreekt"}
              >
                {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? "Verzenden…" : "Verstuur via gekoppelde mailbox"}
              </Button>
            )}
          </div>
          <EmailDispatchHistory rows={ctl.history} prefix="cover-email" />
        </div>
      )}
    </div>
  );
}
